import type { FloorOutlineMode, GeoJSONPolygon } from "@sitescene/types";
import type { Key } from "react-aria-components";

import { Edit05, Image01, Lightning01, Trash01 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import {
	ButtonGroup,
	ButtonGroupItem,
} from "@/components/ui/button-group/button-group";
import { Button } from "@/components/ui/buttons/button";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { ColorPicker } from "@/components/ui/color-picker/color-picker";
import { Form } from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { Slider } from "@/components/ui/slider/slider";
import { TextArea } from "@/components/ui/textarea/textarea";
import { Tooltip } from "@/components/ui/tooltip/tooltip";
import { GeneralModal } from "@/features/modals/general-modal";

import type { ShadowFloor } from "../../../types/location-map";

import { useUpdateFloor } from "../../../hooks/useMapEntityUpdates";
import {
	type FloorOutlineAppearance,
	MAX_FLOOR_BORDER_WIDTH,
	MIN_FLOOR_BORDER_WIDTH,
	normalizeFloorBorderWidth,
} from "../../../map/floors/floor-boundary-overlays";
import { floorImageDisplayUrl } from "../../../map/floors/floor-map-images";
import { toShadowFloorUpdate } from "../../../utils/location/to-shadow-wire";
import { toTitleCase } from "../../../utils/shared/strings";
import { mergeZoneColorSwatches } from "../../../utils/zone/zone-colors";
import { EditScrollBody } from "./edit-scroll-body";

type InlineFloorEditFormProps = {
	appearance: FloorOutlineAppearance;
	/** Custom outline. Null means nothing is drawn yet. */
	boundary?: GeoJSONPolygon | null;
	floor: ShadowFloor;
	/** Auto has a shape to show. False when this floor has no zone geometry. */
	hasAutoOutline?: boolean;
	onAppearanceChange: (appearance: FloorOutlineAppearance) => void;
	onBoundaryChange?: (boundary: GeoJSONPolygon | null) => void;
	onClose: () => void;
	onOutlineModeChange?: (mode: FloorOutlineMode) => void;
	outlineMode: FloorOutlineMode;
};

type InlineFloorEditValues = {
	description: string;
	name: string;
	opacity: string;
};

const parseOpacity = (value: string): null | number => {
	const trimmed = value.trim();
	if (!trimmed) return null;
	const opacity = Number(trimmed);
	if (!Number.isFinite(opacity) || opacity < 0 || opacity > 1) return null;
	return opacity;
};

const firstSelectedKey = (keys: "all" | Set<Key>): Key | undefined => {
	if (keys === "all") return undefined;
	return [...keys][0];
};

/** Map-sidebar floor editor. List view keeps the Manage Floor modal. */
export const InlineFloorEditForm = ({
	appearance,
	boundary = null,
	floor,
	hasAutoOutline = false,
	onAppearanceChange,
	onBoundaryChange,
	onClose,
	onOutlineModeChange,
	outlineMode,
}: InlineFloorEditFormProps) => {
	const updateFloor = useUpdateFloor();
	const [submitError, setSubmitError] = useState<null | string>(null);
	const [clearImage, setClearImage] = useState(false);
	const [imageFileName, setImageFileName] = useState<null | string>(null);
	const [extraSwatchColors, setExtraSwatchColors] = useState<string[]>([]);
	const [isAddColorModalOpen, setIsAddColorModalOpen] = useState(false);
	const [pendingCustomColor, setPendingCustomColor] = useState(
		appearance.color,
	);
	const swatchColors = useMemo(
		() => mergeZoneColorSwatches([appearance.color, ...extraSwatchColors]),
		[appearance.color, extraSwatchColors],
	);
	const lineWidthFormat = useMemo(
		() => ({ maximumFractionDigits: 0 }) as const,
		[],
	);
	const handleColorChange = (hex: string) => {
		const next = hex.trim().toUpperCase().slice(0, 7);
		if (next === appearance.color) return;
		onAppearanceChange({ ...appearance, color: next });
	};
	const hasImage = Boolean(floor.image) && !clearImage;
	const imageIsOnMap = Boolean(
		floor.image && floorImageDisplayUrl(floor.image, floor.imageUrl),
	);
	const outlineModeKeys = useMemo(() => new Set([outlineMode]), [outlineMode]);
	const showOutlineTrash =
		(outlineMode === "auto" && hasAutoOutline) ||
		(outlineMode === "custom" && Boolean(boundary));
	const outlineTrashLabel =
		outlineMode === "auto" ? "Draw a custom outline" : "Redraw outline";

	const {
		control,
		formState: { errors, isValid },
		handleSubmit,
		reset,
	} = useForm<InlineFloorEditValues>({
		defaultValues: {
			description: floor.description ?? "",
			name: toTitleCase(floor.name),
			opacity:
				floor.image?.opacity !== undefined ? String(floor.image.opacity) : "",
		},
		mode: "onChange",
	});

	useEffect(() => {
		queueMicrotask(() => {
			setClearImage(false);
			setImageFileName(null);
			reset({
				description: floor.description ?? "",
				name: toTitleCase(floor.name),
				opacity:
					floor.image?.opacity !== undefined ? String(floor.image.opacity) : "",
			});
		});
	}, [floor.description, floor.id, floor.image, floor.name, reset]);

	const canSubmit = isValid && !updateFloor.isPending;

	const onSubmit = handleSubmit((data) => {
		const trimmedDescription = data.description.trim();
		const image = floor.image;
		const opacity = parseOpacity(data.opacity);

		setSubmitError(null);
		updateFloor.mutate(
			{
				body: toShadowFloorUpdate({
					borderWidth: appearance.borderWidth,
					color: appearance.color,
					description:
						trimmedDescription.length > 0 ? trimmedDescription : null,
					fill: appearance.fill,
					name: data.name.trim(),
					outlineMode,
					...(outlineMode === "custom"
						? { boundary: boundary ?? null }
						: { boundary: null }),
					...(outlineMode === "image"
						? clearImage
							? { image: null }
							: image
								? {
										image: {
											...image,
											...(opacity !== null ? { opacity } : {}),
										},
									}
								: {}
						: {}),
				}),
				floorId: floor.id,
			},
			{
				onError: () => {
					setSubmitError("Could not update floor.");
				},
				onSuccess: () => {
					onClose();
				},
			},
		);
	});

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
			<EditScrollBody>
				<Form
					className="flex flex-col gap-3"
					onSubmit={onSubmit}
					validationBehavior="aria"
				>
					<Controller
						control={control}
						name="name"
						render={({ field }) => (
							<Input
								errorMessage={errors.name?.message}
								isInvalid={Boolean(errors.name)}
								isRequired
								label="Floor Name"
								onBlur={field.onBlur}
								onChange={field.onChange}
								placeholder="Level 1"
								ref={field.ref}
								size="sm"
								value={field.value}
							/>
						)}
						rules={{
							required: "Floor name is required",
							validate: (value) =>
								value.trim().length > 0 || "Floor name is required",
						}}
					/>

					<Controller
						control={control}
						name="description"
						render={({ field }) => (
							<TextArea
								label="Description"
								onChange={field.onChange}
								placeholder="Optional description"
								rows={3}
								value={field.value}
							/>
						)}
					/>

					{floor.image && outlineMode === "image" ? (
						<div className="flex flex-col gap-3 border-t border-secondary pt-3">
							<h3 className="text-sm font-semibold text-primary">Floor plan</h3>
							<p className="text-sm text-tertiary">
								{clearImage
									? "The floor plan will be removed when you save."
									: imageIsOnMap
										? "This image is drawn on the map from its saved corners."
										: "An image is saved. It appears on the map once a display URL is available."}
							</p>
							{hasImage ? (
								<Controller
									control={control}
									name="opacity"
									render={({ field }) => (
										<Input
											errorMessage={errors.opacity?.message}
											hint="0 is invisible, 1 is fully opaque. Leave blank to keep the current value."
											isInvalid={Boolean(errors.opacity)}
											label="Opacity"
											onBlur={field.onBlur}
											onChange={field.onChange}
											placeholder="1"
											ref={field.ref}
											size="sm"
											value={field.value}
										/>
									)}
									rules={{
										validate: (value) =>
											value.trim().length === 0 ||
											parseOpacity(value) !== null ||
											"Opacity must be between 0 and 1",
									}}
								/>
							) : null}
							<Button
								color="secondary"
								onClick={() => setClearImage((current) => !current)}
								size="sm"
								type="button"
							>
								{clearImage ? "Keep floor plan" : "Remove floor plan"}
							</Button>
						</div>
					) : null}

					<div className="flex flex-col gap-2 border-t border-secondary pt-3">
						<p className="text-sm font-medium text-primary">Floor outline</p>
						<div className="flex items-center gap-2">
							<ButtonGroup
								aria-label="Floor outline mode"
								disallowEmptySelection
								onSelectionChange={(keys) => {
									const next = firstSelectedKey(keys);
									if (
										next !== "auto" &&
										next !== "custom" &&
										next !== "image"
									) {
										return;
									}
									if (next === outlineMode) return;
									onOutlineModeChange?.(next);
								}}
								selectedKeys={outlineModeKeys}
								size="sm"
							>
								<ButtonGroupItem iconLeading={Lightning01} id="auto">
									Auto
								</ButtonGroupItem>
								<ButtonGroupItem iconLeading={Edit05} id="custom">
									Custom
								</ButtonGroupItem>
								<ButtonGroupItem iconLeading={Image01} id="image">
									Image
								</ButtonGroupItem>
							</ButtonGroup>
							{showOutlineTrash ? (
								<Tooltip placement="top" title={outlineTrashLabel}>
									<Button
										aria-label={outlineTrashLabel}
										color="secondary"
										iconLeading={Trash01}
										onClick={() => {
											if (outlineMode === "auto") {
												onOutlineModeChange?.("custom");
											}
											onBoundaryChange?.(null);
										}}
										size="sm"
										type="button"
									/>
								</Tooltip>
							) : null}
						</div>
						<p
							className={
								outlineMode === "custom" && !boundary
									? "text-sm text-success-primary"
									: "text-sm text-tertiary"
							}
						>
							{outlineMode === "auto"
								? hasAutoOutline
									? "Wraps the zones on this floor. Drag a vertex to switch to Custom and reshape."
									: "Add zones on this floor to wrap them, or switch to Custom to draw an outline."
								: outlineMode === "custom"
									? boundary
										? "Drag the whole outline to move it, or drag vertices to reshape."
										: "Click the map to place vertices. Double-click or click the first point to finish."
									: "Upload a floor plan. Drawing is off while Image is selected."}
						</p>
						{outlineMode === "image" ? (
							<div className="flex flex-col gap-2">
								<label
									className="text-sm font-medium text-primary"
									htmlFor="floor-plan-upload"
								>
									Floor plan image
								</label>
								<input
									accept="image/png,image/jpeg,image/webp,image/gif"
									className="block w-full text-sm text-secondary file:mr-3 file:rounded-lg file:border file:border-secondary file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary"
									id="floor-plan-upload"
									onChange={(event) => {
										const file = event.target.files?.[0];
										setImageFileName(file?.name ?? null);
									}}
									type="file"
								/>
								<p className="text-sm text-tertiary">
									{imageFileName
										? imageFileName
										: imageIsOnMap
											? "This image is drawn on the map from its saved corners."
											: floor.image
												? "An image is saved. It appears on the map once a display URL is available."
												: "Choose an image to use as the floor plan."}
								</p>
							</div>
						) : null}
						{outlineMode !== "image" ? (
							<div className="flex flex-col gap-3 border-t border-secondary pt-3">
								<div className="flex flex-col gap-2">
									<p className="text-sm font-medium text-primary">
										Floor color
									</p>
									<ColorPicker.Provider
										onChange={(color) =>
											handleColorChange(color.toString("hex"))
										}
										value={appearance.color}
									>
										<ColorPicker.SavedColors
											colors={swatchColors}
											label="Saved"
											onAdd={() => {
												setPendingCustomColor(appearance.color);
												setIsAddColorModalOpen(true);
											}}
											onSelect={handleColorChange}
										/>
									</ColorPicker.Provider>
								</div>
								<div className="flex flex-col gap-2">
									<p className="text-sm font-medium text-primary">
										{appearance.borderWidth === 0
											? "Line width (none)"
											: `Line width (${appearance.borderWidth} px)`}
									</p>
									<Slider
										aria-label="Line width"
										formatOptions={lineWidthFormat}
										maxValue={MAX_FLOOR_BORDER_WIDTH}
										minValue={MIN_FLOOR_BORDER_WIDTH}
										onChange={(value) => {
											const next = Array.isArray(value) ? value[0] : value;
											if (typeof next !== "number") return;
											const borderWidth = normalizeFloorBorderWidth(next);
											if (borderWidth === appearance.borderWidth) return;
											onAppearanceChange({ ...appearance, borderWidth });
										}}
										step={1}
										value={appearance.borderWidth}
									/>
									<p className="text-sm text-tertiary">
										0 hides the border. Otherwise 1–12 pixels.
									</p>
								</div>
								<Checkbox
									hint="Turn off for a completely transparent background."
									isSelected={appearance.fill}
									label="Fill background"
									onChange={(isSelected) => {
										if (isSelected === appearance.fill) return;
										onAppearanceChange({ ...appearance, fill: isSelected });
									}}
									size="sm"
								/>
							</div>
						) : null}
					</div>

					{submitError ? (
						<p className="text-sm text-error-primary">{submitError}</p>
					) : null}
				</Form>
			</EditScrollBody>
			<div className="bg-secondary_subtle flex shrink-0 flex-wrap items-center gap-2 border-t border-secondary px-4 py-3">
				<Button color="secondary" onClick={onClose} size="sm">
					Cancel
				</Button>
				<Button
					color="primary"
					isDisabled={!canSubmit}
					onClick={onSubmit}
					size="sm"
				>
					{updateFloor.isPending ? "Saving..." : "Save"}
				</Button>
			</div>
			<GeneralModal
				closeOnSubmit={false}
				description="Pick a custom color to add to your saved swatches."
				isOpen={isAddColorModalOpen}
				onCancel={() => setIsAddColorModalOpen(false)}
				onClose={() => setIsAddColorModalOpen(false)}
				onSubmit={() => {
					const normalized = pendingCustomColor.trim().toUpperCase();
					if (!/^#[0-9A-F]{6}$/.test(normalized)) return;
					setExtraSwatchColors((current) =>
						current.includes(normalized) ? current : [...current, normalized],
					);
					onAppearanceChange({ ...appearance, color: normalized });
					setIsAddColorModalOpen(false);
				}}
				submitText="Add color"
				title="Add floor color"
			>
				<ColorPicker.Provider
					onChange={(color) => setPendingCustomColor(color.toString("hex"))}
					value={pendingCustomColor}
				>
					<ColorPicker />
				</ColorPicker.Provider>
			</GeneralModal>
		</div>
	);
};
